import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, Save, Loader2, PauseCircle, Send, Eraser, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { SignaturePad } from "@/components/SignaturePad";
import { StepClient, StepChecklist, StepMeasure, StepPhotos, Summary, SignerInput } from "@/pages/agent/steps";
import { api, downloadPdf, errMsg } from "@/lib/api";
import { ChipGroup } from "@/components/ChipGroup";
import { RESULTS } from "@/lib/constants";
import { useLocationReporter } from "@/hooks/useLocationReporter";
import { QuoteCard } from "@/components/QuoteCard";
import { NotificationBell } from "@/components/NotificationBell";
import { RatingLinkDialog } from "@/components/RatingLinkDialog";

const STEPS = ["العميل", "الجاهزية", "المقاسات", "الصور", "التوقيع"];

export default function InspectionWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const sig = useRef(null);
  const [insp, setInsp] = useState(null);
  const [ch, setCh] = useState({});
  const [m, setM] = useState({});
  const [result, setResult] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [signer, setSigner] = useState("");
  const [hasSig, setHasSig] = useState(false);
  const [hold, setHold] = useState({ open: false, reason: "" });
  const [rate, setRate] = useState(false);
  useLocationReporter();

  useEffect(() => {
    api.get(`/inspections/${id}`).then(({ data }) => {
      setInsp(data);
      setCh(data.checklist || {});
      setM(data.measurements || {});
      setResult(data.result || {});
      setSigner(data.customer.name);
    }).catch((e) => {
      toast.error(errMsg(e));
      nav("/agent");
    });
  }, [id, nav]);

  if (!insp) return <div className="dark min-h-screen bg-ink grid place-items-center"><Loader2 className="animate-spin text-brand-red" /></div>;

  const done = insp.status === "completed";
  const save = async (quiet) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/inspections/${id}/form`, { checklist: ch, measurements: m, result });
      setInsp((x) => ({ ...data, photos: x.photos }));
      if (!quiet) toast.success("تم حفظ المسودة");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const go = async (n) => {
    if (!done && step > 0) await save(true);
    if (!done && step === 0 && insp.status !== "in_progress") {
      const { data } = await api.post(`/inspections/${id}/start`).catch(() => ({ data: null }));
      if (data) setInsp(data);
    }
    setStep(n);
  };

  const submitHold = async () => {
    if (!hold.reason.trim()) return toast.error("اكتب سبب التعليق");
    try {
      await save(true);
      await api.post(`/inspections/${id}/hold`, { reason: hold.reason });
      toast.success("تم تعليق المعاينة وإبلاغ الإدارة");
      nav("/agent");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submit = async () => {
    if (!m.height || !m.width || !m.door_type) return toast.error("أدخل الارتفاع والعرض ونوع الباب أولاً");
    if (!result.outcome) return toast.error("اختر نتيجة المعاينة");
    if (!hasSig) return toast.error("يرجى أخذ توقيع العميل");
    if (!signer.trim()) return toast.error("اكتب اسم الموقّع");
    setSaving(true);
    try {
      await api.put(`/inspections/${id}/form`, { checklist: ch, measurements: m, result });
      await api.post(`/inspections/${id}/submit`, { signer_name: signer, signature: sig.current.toDataURL() });
      toast.success("تم إرسال التقرير للإدارة");
      nav("/agent");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const setC = (k, v) => !done && setCh((x) => ({ ...x, [k]: v }));
  const setMe = (k, v) => !done && setM((x) => ({ ...x, [k]: v }));

  return (
    <div className="dark min-h-screen bg-ink text-slate-100 pb-28">
      <header className="sticky top-0 z-20 bg-ink/90 backdrop-blur-md border-b border-line">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/agent" aria-label="رجوع" data-testid="wizard-back-link" className="h-11 w-11 grid place-items-center rounded-lg border border-line hover:bg-panel-hover"><ChevronRight size={20} /></Link>
          <div className="me-auto">
            <p className="font-mono text-xs text-brand-sky" data-testid="wizard-ref">{insp.ref}</p>
            <p className="font-heading font-bold">{STEPS[step]}</p>
          </div>
          <StatusBadge status={insp.status} dark testId="wizard-status" />
          <NotificationBell dark loud />
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3 grid grid-cols-5 gap-1.5">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => go(i)} data-testid={`wizard-step-${i}`} className="text-start">
              <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-brand-red" : "bg-line"}`} />
              <p className={`text-[11px] mt-1 ${i === step ? "text-brand-sky font-bold" : "text-slate-500"}`}>{s}</p>
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {done && (
          <div className="mb-5 rounded-xl border border-emerald-700 bg-emerald-950/60 p-4 text-sm flex items-center justify-between gap-3">
            <span>تم إرسال هذه المعاينة. للعرض فقط.</span>
            <button onClick={() => downloadPdf(insp.id, insp.ref)} data-testid="agent-pdf-button" className="h-10 px-3 rounded-lg bg-emerald-600 text-white font-bold flex items-center gap-1.5"><FileDown size={16} />PDF</button>
          </div>
        )}
        {done && !insp.rating && (
          <button onClick={() => setRate(true)} data-testid="agent-rating-link-button"
            className="mb-5 w-full h-12 rounded-lg border border-amber-500/70 text-amber-300 font-bold hover:bg-amber-500/10">
            {insp.rating_link_sent_at ? "إعادة إرسال رابط التقييم للعميل" : "أرسل للعميل رابط تقييم الزيارة"}
          </button>
        )}
        {done && insp.quote && <QuoteCard insp={insp} dark />}
        {done && !insp.quote && <p className="mb-5 text-xs text-slate-400" data-testid="agent-quote-pending">سيظهر هنا عرض السعر المبدئي عندما تصدره الإدارة، ويصلك إشعار بذلك.</p>}
        {done && insp.rating && <p className="mb-5 text-sm text-amber-300" data-testid="agent-rating-received">قيّمك العميل: {"★".repeat(insp.rating.agent)} ({insp.rating.agent}/5)</p>}
        <RatingLinkDialog open={rate} onClose={() => setRate(false)} insp={insp} onSent={(d) => setInsp((x) => ({ ...d, photos: x.photos }))} />
        {step === 0 && <StepClient insp={insp} setInsp={setInsp} />}
        {step === 1 && <StepChecklist ch={ch} set={setC} />}
        {step === 2 && <StepMeasure m={m} set={setMe} />}
        {step === 3 && <StepPhotos insp={insp} setInsp={setInsp} readOnly={done} />}
        {step === 4 && (
          <div className="space-y-5" data-testid="step-signature">
            <div className="space-y-3" data-testid="result-section">
              <p className="font-bold text-sm text-slate-300">نتيجة المعاينة *</p>
              <ChipGroup options={RESULTS} value={result.outcome} onChange={(v) => !done && setResult((r) => ({ ...r, outcome: v }))} cols="grid-cols-1 sm:grid-cols-2" testId="result-outcome" />
              <Textarea value={result.notes || ""} onChange={(e) => !done && setResult((r) => ({ ...r, notes: e.target.value }))} readOnly={done}
                placeholder="ماذا تم في الزيارة؟ (مثال: تم رفع المقاسات وشرح الخيارات للعميل...)" className="bg-panel border-line" data-testid="result-notes-input" />
            </div>
            <Summary insp={insp} ch={ch} m={m} />
            {done ? (
              <p className="text-sm text-slate-400">وقّع العميل: {insp.signature?.signer_name}</p>
            ) : (
              <>
                <SignerInput value={signer} onChange={setSigner} />
                <SignaturePad ref={sig} onChange={setHasSig} />
                <button onClick={() => sig.current.clear()} data-testid="signature-clear-button" className="h-10 px-3 rounded-lg border border-line text-sm flex items-center gap-1.5"><Eraser size={15} />مسح التوقيع</button>
                <p className="text-xs text-slate-500">بالتوقيع يقر العميل بمراجعة المقاسات والملاحظات والموافقة عليها.</p>
              </>
            )}
          </div>
        )}
        {!done && step === 1 && (
          <button onClick={() => setHold({ open: true, reason: ch.site_notes || "" })} data-testid="hold-inspection-button"
            className="mt-6 w-full h-12 rounded-lg border border-amber-700 text-amber-300 font-bold flex items-center justify-center gap-2 hover:bg-amber-950/50">
            <PauseCircle size={18} />تعليق المعاينة (الموقع غير جاهز)
          </button>
        )}
      </main>

      <footer className="fixed bottom-0 inset-x-0 z-20 bg-ink/95 backdrop-blur-md border-t border-line">
        <div className="max-w-2xl mx-auto px-4 py-3 flex gap-2">
          {step > 0 && <button onClick={() => go(step - 1)} data-testid="wizard-prev-button" className="h-12 w-12 shrink-0 grid place-items-center rounded-lg border border-line hover:bg-panel-hover"><ChevronRight size={20} /></button>}
          {!done && step > 0 && <button onClick={() => save(false)} disabled={saving} data-testid="wizard-save-button" className="h-12 px-4 rounded-lg border border-line flex items-center gap-1.5 font-semibold hover:bg-panel-hover disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}حفظ</button>}
          {step < 4 ? (
            <button onClick={() => go(step + 1)} data-testid="wizard-next-button" className="flex-1 h-12 rounded-lg bg-brand-red text-white font-bold flex items-center justify-center gap-1.5 hover:bg-brand-red-dark">
              {step === 0 && !done ? "بدء المعاينة" : "التالي"}<ChevronLeft size={18} />
            </button>
          ) : !done && (
            <button onClick={submit} disabled={saving} data-testid="wizard-submit-button" className="flex-1 h-12 rounded-lg bg-brand-red text-white font-bold flex items-center justify-center gap-2 hover:bg-brand-red-dark disabled:opacity-60">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}إرسال التقرير للإدارة
            </button>
          )}
        </div>
      </footer>

      <Dialog open={hold.open} onOpenChange={(o) => setHold((h) => ({ ...h, open: o }))}>
        <DialogContent dir="rtl" className="dark bg-ink border-line text-slate-100" data-testid="hold-dialog">
          <DialogHeader><DialogTitle className="text-start">سبب تعليق المعاينة</DialogTitle></DialogHeader>
          <Textarea value={hold.reason} onChange={(e) => setHold((h) => ({ ...h, reason: e.target.value }))} placeholder="مثال: الفتحة غير مكتملة البناء، لا يوجد تمديد كهرباء" className="bg-panel border-line" data-testid="hold-reason-input" />
          <button onClick={submitHold} data-testid="hold-confirm-button" className="h-12 rounded-lg bg-brand-red text-white font-bold hover:bg-brand-red-dark">تعليق وإبلاغ الإدارة</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
